import React, { useEffect, useState, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Unstable_Grid2";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import CSDataTable from "../../components/CSDataTable";
import { CSDataTableColumns } from "../../modal/CSDataTable";
import { convertToDateMonthFormat } from "../../utils/ConvertDate";
import { DisplayResponsePlaybook } from "./components/DisplayResponsePlaybook";
import DialogBoxComponent from "../../components/DialogBoxComponent";
import {
  createIncidentResponsePlaybook,
  fetchIncidentResponsePlaybook,
  fetchIncidentResponsePlaybookTemplate,
  updateIncidentResponsePlaybook,
  fetchIncidentResponsePlaybookDetails,
} from "./helper";
import { useUserContext } from "../../context/auth/UserContext";
import { useIncidentResponsePlaybookContext } from "../../context/IncidentResponsePlaybook/IncidentResponsePlaybookContext";
import { Box, CircularProgress } from "@mui/material";
import ImportDialogBox from "../../components/ImportDialogBox";
import DuplicateDialogBox from "../../components/DuplicateDialogBox";
import { useAccessRights } from "../../utils/useAccessRights";

const columns: CSDataTableColumns[] = [
  {
    accessorKey: "playbookName",
    header: "Name",
    size: "80px",
  },
  {
    accessorKey: "version",
    header: "Version",
    size: "60px",
  },
  {
    accessorKey: "status",
    header: "Status",
    size: "80px",
  },
  {
    accessorKey: "approvedDate",
    header: "Approved",
    size: "80px",
  },
];

export const IncidentResponsePlaybook = () => {
  const navigate = useNavigate();
  const incidentResponsePlayBookAccess = useAccessRights(
    "Incident Response PlayBook"
  );

  const [responsePlaybookList, setResponsePlaybookList] = useState([]);
  const [latestVersion, setLatestVersion] = useState<any>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogImportState, setDialogImportState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openImportDialog: false,
  });
  const [dialogDuplicateState, setDialogDuplicateState] = useState({
    title: "",
    body: "",
    isBtnsDisabled: false,
    openDuplicateDialog: false,
  });
  const [dialogState, setDialogState] = useState({
    title: "",
    body: "",
    action: "",
    actionCancel: "",
    openSuccessDialogBox: false,
  });

  const { userId, companyProfileID } = useUserContext();
  const { setPlaybookDetails, setSelectedIncidentResponsePlaybook } =
    useIncidentResponsePlaybookContext();

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectRow = (selectedRows: any) => {
    setSelectedIncidentResponsePlaybook(selectedRows);
  };

  const displayAddPlaybook = () => {
    setSelectedIncidentResponsePlaybook([]);
    setPlaybookDetails({});
    navigate("/incident-response-playbook-form");
  };

  const handleDialogCancel = () => {
    if (dialogState.title === "Success") {
      fetchTableData();
    }
    setDialogState({
      ...dialogState,
      openSuccessDialogBox: false,
    });
    setDialogImportState({
      ...dialogState,
      isBtnsDisabled: false,
      openImportDialog: false,
    });
    setDialogDuplicateState({
      ...dialogState,
      isBtnsDisabled: false,
      openDuplicateDialog: false,
    });
  };

  const displayImportPlaybook = () => {
    setDialogImportState({
      title: "Import Incident Response Playbook",
      body: `By importing the Ransomware Playbook will overwrite the content in any existing one for your organization.`,
      isBtnsDisabled: false,
      openImportDialog: true,
    });
  };

  const handleImportPlaybook = async () => {
    setIsLoading(true);
    setDialogImportState({
      ...dialogImportState,
      isBtnsDisabled: true,
    });
    try {
      const importTemplates = await fetchIncidentResponsePlaybookTemplate();
      const isExsistingPlaybook = responsePlaybookList.filter(
        (ele: any) => ele.playbookName === importTemplates[0].playbookName
      );

      let hasError = false;
      const date = new Date();

      if (isExsistingPlaybook.length > 0) {
        const payload = [
          {
            playbookName: importTemplates[0].playbookName,
            preparation: importTemplates[0].preparation,
            detection: importTemplates[0].detection,
            analysis: importTemplates[0].analysis,
            containment: importTemplates[0].containment,
            eradication: importTemplates[0].eradication,
            recovery: importTemplates[0].recovery,
            reporting: importTemplates[0].reporting,
            version: importTemplates[0].version,
            dateApproved: null,
            status: "Not Approved",
            updatedDate: date,
            updatedBy: userId,
            isDeleted: false,
            playbookID: isExsistingPlaybook[0].playbookID,
            companyProfileID: companyProfileID,
            isEditedByUserId: "0",
          },
        ];

        const resUpdate = await updateIncidentResponsePlaybook(payload);
        // console.log("resUpdate",resUpdate)
        if (!resUpdate.succeeded) {
          hasError = true;
        }
      } else {
        const payload = [
          {
            playbookName: importTemplates[0].playbookName,
            preparation: importTemplates[0].preparation,
            detection: importTemplates[0].detection,
            analysis: importTemplates[0].analysis,
            containment: importTemplates[0].containment,
            eradication: importTemplates[0].eradication,
            recovery: importTemplates[0].recovery,
            reporting: importTemplates[0].reporting,
            version: importTemplates[0].version,
            dateApproved: null,
            status: "Not Approved",
            createDate: date,
            createdBy: userId,
            approvedBy: 0,
            companyProfileID: companyProfileID,
            isEditedByUserId: "0",
          },
        ];
        const resImport = await createIncidentResponsePlaybook(payload);
        // console.log("resImport", resImport);
        if (!resImport.succeeded) {
          hasError = true;
        }
      }

      if (hasError) {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      } else {
        setSelectedIncidentResponsePlaybook([]);
        setPlaybookDetails({});
        setDialogState({
          title: "Success",
          body: "Playbook successfully imported",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setIsLoading(false);
      setDialogImportState({
        ...dialogImportState,
        isBtnsDisabled: false,
      });
    }
  };

  const displayDuplicateConfirm = () => {
    handleClose();
    const _latestVersion = responsePlaybookList.sort(
      (a: any, b: any) => parseFloat(b.version) - parseFloat(a.version)
    )[0];
    // console.log("latestVersion", _latestVersion)
    setLatestVersion(_latestVersion);
    setDialogDuplicateState({
      title: `Duplicate Incident Response Playbook`,
      body: `Are you sure you want to duplicate lastest version <strong>${_latestVersion.version}</strong> of Incident Response Playbook?`,
      openDuplicateDialog: true,
      isBtnsDisabled: false,
    });
  };

  const duplicatePlaybook = async () => {
    const latestVersionDetails = await fetchIncidentResponsePlaybookDetails(
      latestVersion.playbookID
    );

    if (!latestVersionDetails) {
      setDialogState({
        title: "Error",
        body: "Unexpected Error",
        action: "OK",
        actionCancel: "",
        openSuccessDialogBox: true,
      });
      return;
    }
    setDialogDuplicateState({
      ...dialogDuplicateState,
      isBtnsDisabled: true,
    });
    try {
      const date = new Date();
      const payload = [
        {
          playbookName: latestVersionDetails.playbookName,
          preparation: latestVersionDetails.preparation,
          detection: latestVersionDetails.detection,
          analysis: latestVersionDetails.analysis,
          containment: latestVersionDetails.containment,
          eradication: latestVersionDetails.eradication,
          recovery: latestVersionDetails.recovery,
          reporting: latestVersionDetails.reporting,
          version: latestVersionDetails.version,
          dateApproved: null,
          status: "Not Approved",
          createDate: date,
          createdBy: userId,
          approvedBy: 0,
          companyProfileID: companyProfileID,
          isEditedByUserId: "0",
        },
      ];

      const res = await createIncidentResponsePlaybook(payload);
      if (res.succeeded) {
        setSelectedIncidentResponsePlaybook([]);
        handleDialogCancel();
        fetchTableData();
        setDialogState({
          title: "Success",
          body: "Playbook successfully added",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      } else {
        setDialogState({
          title: "Error",
          body: "Unexpected Error",
          action: "OK",
          actionCancel: "",
          openSuccessDialogBox: true,
        });
      }
    } finally {
      setDialogDuplicateState({
        ...dialogDuplicateState,
        isBtnsDisabled: false,
      });
    }
  };

  const fetchTableData = async () => {
    setIsLoading(true);
    try {
      const res = await fetchIncidentResponsePlaybook(companyProfileID);
      const list = res.map((ele: any) => ({
        ...ele,
        rowId: ele.playbookID,
        approvedDate: convertToDateMonthFormat(ele.dateApproved),
      }));
      setResponsePlaybookList(list);
    } catch (error) {
      console.log("@@Error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, []);

  return (
    <Fragment>
      <Grid container sx={{ height: "100%", width: "100%" }}>
        <Grid
          xs={6}
          style={{
            padding: "1rem",
            margin: "1rem 0",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid xs={3}>
              <h3 style={{ fontWeight: "400" }}>
                Incident Response Playbook ({responsePlaybookList.length})
              </h3>
            </Grid>
            <Grid>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayAddPlaybook}
                disabled={
                  !incidentResponsePlayBookAccess?.isCreate || isLoading
                }
              >
                Create New Playbook
              </Button>
              <Button
                size="small"
                variant="contained"
                className="table-btn"
                onClick={displayImportPlaybook}
                disabled={
                  !incidentResponsePlayBookAccess?.isImport || isLoading
                }
              >
                Import
              </Button>
              <IconButton
                aria-label="menu"
                disabled={isLoading}
                onClick={handleClick}
              >
                <MoreVertIcon />
              </IconButton>
              <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
              >
                <MenuItem
                  disabled={!incidentResponsePlayBookAccess?.isCreate}
                  onClick={displayDuplicateConfirm}
                >
                  Duplicate Playbook
                </MenuItem>
              </Menu>
            </Grid>
          </Grid>

          {isLoading ? (
            <Box display="flex" justifyContent="center">
              <CircularProgress />
            </Box>
          ) : (
            <CSDataTable
              rows={responsePlaybookList}
              columns={columns}
              multiRowSelection={false}
              // previewRecord={responsePlaybookList[0]}
              handleSelectRow={(selectedRows) => handleSelectRow(selectedRows)}
            />
          )}
        </Grid>

        <Grid
          xs
          style={{
            padding: "1rem",
            margin: "1rem",
            background: "#FFF",
            borderRadius: "2%",
          }}
        >
          <DisplayResponsePlaybook refreshList={() => fetchTableData()} />
        </Grid>
      </Grid>
      {
        <ImportDialogBox
          openDialogBox={dialogImportState.openImportDialog}
          dialogTitle={dialogImportState.title}
          dialogBody={dialogImportState.body}
          isBtnsDisabled={dialogImportState.isBtnsDisabled}
          handleFunction={handleImportPlaybook}
          handleCancel={handleDialogCancel}
        />
      }
      {
        //Duplicate Dialog
        <DuplicateDialogBox
          openDialogBox={dialogDuplicateState.openDuplicateDialog}
          dialogTitle={dialogDuplicateState.title}
          dialogBody={dialogDuplicateState.body}
          isBtnsDisabled={dialogDuplicateState.isBtnsDisabled}
          handleFunction={duplicatePlaybook}
          handleCancel={handleDialogCancel}
        />
      }
      {
        //Success Dialog
        <DialogBoxComponent
          openDialogBox={dialogState.openSuccessDialogBox}
          dialogTitle={dialogState.title}
          dialogAction={dialogState.action}
          dialogBody={dialogState.body}
          handleFunction={handleDialogCancel}
        />
      }
    </Fragment>
  );
};
